import React, { useEffect, useState, useRef } from "react";
import { Modal, Popconfirm } from "antd";
import { useGlobalContext } from "../context/AuthContext";
import {
  CREATE_SUBJECT,
  UPDATE_SUBJECT,
  DELETE_SUBJECT,
  GET_ALL_SUBJECTS,
} from "../../services/graphql/queriesMutations";
import { useMutation } from "@apollo/client";
import CreateGistModal from "./CreateGistModal";

interface Subject {
  id: number;
  title: string;
  parentId: number | null;
}

interface NodeActionModalProps {
  visible: boolean;
  position: { x: number; y: number };
  node: Subject | null;
  onClose: () => void;
  setModalAction: (action: string) => void;
  modalAction: string;
}

const modalStyles = {
  mask: {
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(4px)',
  },
  content: {
    borderRadius: 8,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    border: '1px solid var(--color-border)',
  },
};

const NodeActionModal: React.FC<NodeActionModalProps> = ({
  visible,
  position,
  node,
  onClose,
  setModalAction,
  modalAction,
}) => {
  const [createNewSubject, { loading: createLoading }] = useMutation(CREATE_SUBJECT, {
    refetchQueries: [{ query: GET_ALL_SUBJECTS }],
  });
  const [updateSubject, { loading: updateLoading }] = useMutation(UPDATE_SUBJECT, {
    refetchQueries: [{ query: GET_ALL_SUBJECTS }],
  });
  const [deleteSubject] = useMutation(DELETE_SUBJECT, {
    refetchQueries: [{ query: GET_ALL_SUBJECTS }],
  });

  const { user } = useGlobalContext();
  const isAdmin = user?.userType === "admin";
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");

  const isLoading = createLoading || updateLoading;

  // Initialize input value when modal opens
  useEffect(() => {
    if (visible && node) {
      console.log("🎯 NodeActionModal opened with modalAction:", modalAction);
      if (modalAction === "update") {
        setInputValue(node.title);
      } else {
        setInputValue("");
      }
      setError("");
    }
  }, [visible, node, modalAction]);

  // Focus input for add/update
  useEffect(() => {
    if ((modalAction === "add" || modalAction === "update") && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [modalAction, visible]);

  // Handle delete confirmation
  const handleDelete = async () => {
    if (!node) return;
    try {
      await deleteSubject({ variables: { id: node.id } });
      onClose();
    } catch (e: any) {
      console.error("Delete failed:", e);
      setError(e.message || "Failed to delete");
    }
  };

  // Handle form submit
  const handleSubmit = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) {
      setError("Title cannot be empty");
      return;
    }

    try {
      if (modalAction === "add") {
        await createNewSubject({
          variables: { subject: { title: trimmed, parentId: node?.id } },
        });
      } else if (modalAction === "update") {
        if (trimmed === node?.title) {
          onClose();
          return;
        }
        await updateSubject({
          variables: { id: node?.id, subject: { title: trimmed } },
        });
      }
      onClose();
    } catch (e: any) {
      setError(e.message || "Operation failed");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSubmit();
    if (e.key === "Escape") onClose();
  };

  if (!user || !node) return null;

  if (["add", "update", "delete"].includes(modalAction) && !isAdmin) {
    return (
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width={340}
        centered
        maskStyle={modalStyles.mask}
        styles={{ content: modalStyles.content }}
      >
        <div style={{ fontFamily: 'var(--font-primary)', textAlign: 'center', padding: '8px 0' }}>
          <h3 style={{ color: 'var(--color-terracotta)', marginBottom: 12 }}>Admin only</h3>
          <p style={{ color: 'var(--color-charcoal)', marginBottom: 20 }}>
            This subject-tree action requires administrator privileges.
          </p>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              borderRadius: 6,
              border: 'none',
              backgroundColor: 'var(--color-forest)',
              color: 'white',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </Modal>
    );
  }

  // ============== SWITCH LOGIC: Show only the relevant form ==============

  // 1. CREATE GIST MODAL
  if (modalAction === "show") {
    return (
      <CreateGistModal
        visible={visible}
        onClose={onClose}
        subjectId={node.id}
      />
    );
  }

  // 2. DELETE CONFIRMATION
  if (modalAction === "delete") {
    return (
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width={320}
        centered
        maskStyle={modalStyles.mask}
        styles={{ content: modalStyles.content }}
      >
        <div style={{ fontFamily: 'var(--font-primary)', textAlign: 'center', padding: '8px 0' }}>
          <h3 style={{ color: 'var(--color-terracotta)', marginBottom: 16 }}>
            Delete Subject?
          </h3>
          <p style={{ color: 'var(--color-charcoal)', marginBottom: 20 }}>
            Are you sure you want to delete <strong>"{node.title}"</strong>?<br />
            This action cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 20px',
                borderRadius: 6,
                border: '1px solid var(--color-border)',
                backgroundColor: 'white',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              style={{
                padding: '8px 20px',
                borderRadius: 6,
                border: 'none',
                backgroundColor: 'var(--color-terracotta)',
                color: 'white',
                cursor: 'pointer',
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // 3. RENAME FORM (modalAction === "update")
  if (modalAction === "update") {
    return (
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width={360}
        centered
        title={<span style={{ color: 'var(--color-forest)' }}>✏️ Rename Subject</span>}
        maskStyle={modalStyles.mask}
        styles={{ content: modalStyles.content }}
      >
        <div style={{ fontFamily: 'var(--font-primary)' }}>
          <p style={{ color: 'var(--color-gray-warm)', fontSize: 13, marginBottom: 12 }}>
            Current name: <strong>{node.title}</strong>
          </p>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => { setInputValue(e.target.value); setError(""); }}
            onKeyDown={handleKeyDown}
            placeholder="Enter new name"
            style={{
              width: '100%',
              padding: '10px 12px',
              fontSize: 14,
              border: error ? '1px solid var(--color-terracotta)' : '1px solid var(--color-border)',
              borderRadius: 6,
              marginBottom: 8,
            }}
          />
          {error && <p style={{ color: 'var(--color-terracotta)', fontSize: 12 }}>{error}</p>}
          <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              disabled={isLoading}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: '1px solid var(--color-border)',
                backgroundColor: 'white',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading || !inputValue.trim()}
              style={{
                padding: '8px 20px',
                borderRadius: 6,
                border: 'none',
                backgroundColor: 'var(--color-forest)',
                color: 'white',
                cursor: 'pointer',
                opacity: isLoading || !inputValue.trim() ? 0.5 : 1,
              }}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // 4. ADD SUB-SUBJECT FORM (modalAction === "add")
  if (modalAction === "add") {
    return (
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width={360}
        centered
        title={<span style={{ color: 'var(--color-forest)' }}>➕ Add Sub-subject</span>}
        maskStyle={modalStyles.mask}
        styles={{ content: modalStyles.content }}
      >
        <div style={{ fontFamily: 'var(--font-primary)' }}>
          <p style={{ color: 'var(--color-gray-warm)', fontSize: 13, marginBottom: 12 }}>
            Parent: <strong>{node.title}</strong>
          </p>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => { setInputValue(e.target.value); setError(""); }}
            onKeyDown={handleKeyDown}
            placeholder="Enter sub-subject name"
            style={{
              width: '100%',
              padding: '10px 12px',
              fontSize: 14,
              border: error ? '1px solid var(--color-terracotta)' : '1px solid var(--color-border)',
              borderRadius: 6,
              marginBottom: 8,
            }}
          />
          {error && <p style={{ color: 'var(--color-terracotta)', fontSize: 12 }}>{error}</p>}
          <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              disabled={isLoading}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: '1px solid var(--color-border)',
                backgroundColor: 'white',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading || !inputValue.trim()}
              style={{
                padding: '8px 20px',
                borderRadius: 6,
                border: 'none',
                backgroundColor: 'var(--color-forest)',
                color: 'white',
                cursor: 'pointer',
                opacity: isLoading || !inputValue.trim() ? 0.5 : 1,
              }}
            >
              {isLoading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // 5. DEFAULT: No modal (modalAction === "none")
  return null;
};

export default NodeActionModal;
